import { useEffect, useState } from "react";
import { ChromePicker, CirclePicker, Color, ColorChangeHandler, ColorResult } from "react-color";
import { Tooltip } from "@mui/material";

import color_selector from "@/main/assets/images/common/color_selector.png";

import * as S from "./styles";
import { getTranslateByKey } from "@/main/services/utils";
import { handleKeyActivate } from "@/main/services/utils";

type Props = {
	onChange: (color: string) => void;
	value?: string;
	id?: string;
};

export function ColorPicker({ value, onChange, id }: Props) {
	const [colorSelected, setColorSelected] = useState<string | undefined>(value);

	function handleOnChangeComplete(value: ColorResult) {
		onChange(value.hex);
		setColorSelected(value.hex);
	}

	useEffect(() => {
		setColorSelected(value);
	}, [value]);

	const isCustomColor = colorSelected && !PALETTE_COLORS.includes(colorSelected.toLowerCase());

	return (
		<S.Container $colorSelected={colorSelected}>
			<CustomItem onChangeComplete={handleOnChangeComplete} id={id} currentColor={colorSelected} />
			{isCustomColor && <S.ColorPreview $color={colorSelected} title={colorSelected} />}
			<CirclePicker colors={PALETTE_COLORS} width="100%" onChange={handleOnChangeComplete} color={colorSelected} />
		</S.Container>
	);
}

type PropsCustomItem = {
	onChangeComplete?: ColorChangeHandler;
	id?: string;
	currentColor?: string;
};

function CustomItem({ onChangeComplete, id, currentColor }: PropsCustomItem) {
	const [color, setColor] = useState<Color | undefined>(currentColor);
	const [showPicker, setShowPicker] = useState(false);

	useEffect(() => {
		if (currentColor) {
			setColor(currentColor);
		}
	}, [currentColor]);

	const handleImageClick = () => {
		setShowPicker(!showPicker);
	};

	const handleColorChange = (newColor: ColorResult) => {
		setColor(newColor.hex);
	};

	return (
		<div style={{ position: "relative", display: "flex" }}>
			<Tooltip title={getTranslateByKey("information.selectMoreColors")}>
				<span
					role="button"
					tabIndex={0}
					onKeyDown={handleKeyActivate(() => handleImageClick())}
					onClick={handleImageClick}
					{...(id ? { id: `${id}-color-selector` } : {})}
				>
					<img src={color_selector} alt="color-img" height={28} />
				</span>
			</Tooltip>

			{showPicker && (
				<>
					<div
						style={{
							position: "absolute",
							top: "100%",
							marginTop: 10,
							zIndex: 10,
						}}
					>
						<ChromePicker color={color} onChange={handleColorChange} onChangeComplete={onChangeComplete} disableAlpha />
					</div>
					<div
						role="presentation"
						style={{
							position: "fixed",
							inset: 0,
							zIndex: 1,
						}}
						onClick={() => setShowPicker(false)}
						{...(id ? { id: `${id}-picker-overlay` } : {})}
					/>
				</>
			)}
		</div>
	);
}

const PALETTE_COLORS = [
	"#7763f3",
	"#87cd1d",
	"#48d4c0",
	"#f0447b",
	"#abebd3",
	"#ead87c",
	"#e9e3f0",
	"#2896ef",
	"#f97a22",
	"#2ac981",
	"#f696b5",
	"#9ea1f8",
	"#db50ef",
	"#facb12",
];
